import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { css } from "@tento-ui/component-styles";
import { Typography } from "@tento-ui/ui/typography";

/** `kv-grid`, in the dsh register — a record's key/value pairs, on the same
 * card `term-block` draws.
 *
 * The native skin renders this through `std-property-list`, which is the
 * app's key/value component and brings a label column, hover rows and a
 * detail-pane density with it. dsh wants none of those: a key/value block
 * here is a BLOCK, the twin of a terminal block, and the thing that makes it
 * read as one is that the two share a radius, a hairline, a fill and a
 * monospace face. A property list inside that card would be a second surface
 * stacked on the first and a second interaction model on a row that has none.
 *
 * So this is a two-column CSS grid and nothing more, and the grid is what
 * earns its place: `max-content 1fr` gives every value in the block ONE left
 * edge, which is the whole reason a reader scans a kv block rather than
 * reading it. A flex row per pair — the shape this reaches for first — starts
 * each value wherever its own key happened to end, and three pairs then land
 * their values at three different x.
 *
 * Keys sit one gray step dimmer than values. That is the inverse of a form,
 * and it is right for the same reason: the key is the thing you already know
 * you are looking for, and the value is the thing you came to read.
 *
 * Both cells are monospace at the fine size, because a kv block's values are
 * ids, paths and counts — columns a reader compares down, not prose. */
@component({ name: "std-dsh-kv-grid" })
export class DshKvGrid extends LonaWebComponent {
  private $grid: HTMLElement = this.$("grid");

  static makeWith(props: DshKvGrid.Props): DshKvGrid {
    return (DshKvGrid.make() as DshKvGrid).bind(props);
  }

  /** Idempotent by construction: the grid's whole child list is REPLACED, so
   * a pooled instance carries none of the pairs it used to hold. */
  bind(props: DshKvGrid.Props): this {
    const $cells: HTMLElement[] = [];
    for (const [key, value] of props.pairs) {
      const $key = Typography.small(key);
      $key.className = "small key";
      const $value = Typography.small(value);
      $value.className = "small value";
      $cells.push($key, $value);
    }
    this.$grid.replaceChildren(...$cells);
    // An empty card is a hairline rectangle with nothing in it, which reads
    // as a broken row rather than as an absent one. A producer should not
    // send this; a row still has to survive it.
    this.toggleAttribute("empty", props.pairs.length === 0);
    return this;
  }

  static $styles = [
    css`
      :host {
        display: block;
        width: 100%;
        min-width: 0;
      }
      :host([empty]) #frame {
        display: none;
      }
      /* The same card term-block draws — same radius, same hairline, same
         fill. Two components, one surface, on purpose. */
      #frame {
        box-sizing: border-box;
        border: 1px solid var(--divider, rgba(127, 127, 127, 0.25));
        border-radius: 12px;
        background: var(--bg-hover, rgba(127, 127, 127, 0.06));
        padding: 12px 16px;
        overflow: hidden;
      }
      /* max-content on the keys, so the column is exactly as wide as the
         longest one and every value starts at the same x. 1fr on the values,
         so a long one wraps inside its own column instead of widening the
         block. start rather than center: the key is one line and a value may
         be several, and aligning to the FIRST line is the only rule that does
         not change meaning when a value wraps. */
      #grid {
        display: grid;
        grid-template-columns: max-content 1fr;
        align-items: start;
        column-gap: 14px;
        row-gap: 4px;
        min-width: 0;
      }
      /* Both cells: one size, one face. The ramp reads --font-size, divides
         it by the face cap height and trims the block's margins from the
         result — a literal font-size here would win the size and leave the
         trim computed off a different number, which is how a grid ends up
         with rows that do not sit on their own ink. */
      #grid .key,
      #grid .value {
        margin: 0;
        min-width: 0;
        --font-size: var(--turn-fine-size, var(--text-8px));
        --line-height-ratio: 1.5;
        --margin-top: 0px;
        --margin-bottom: 0px;
        font-family: var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace);
      }
      /* The key is nowrap because a wrapped key would widen the max-content
         column for every row in the block. A key long enough to need wrapping
         is a producer problem, and the ellipsis says so. */
      #grid .key {
        color: var(--text-ter, #8b93a7);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      #grid .value {
        color: var(--text-sec, #6a7186);
        overflow-wrap: anywhere;
        font-variant-numeric: tabular-nums;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <div id="frame">
      <div id="grid"></div>
    </div>
  `;
}

export namespace DshKvGrid {
  export type Props = {
    /** Ordered pairs, as the record carries them. */
    pairs: [string, string][];
  };
}

/** The long form of the name, for a caller that spells this slot's component
 * the way the turn family spells its own. One class, two export names — the
 * skin map may reach for either. */
export { DshKvGrid as DshTurnKvGrid };
